import React, { useState, useEffect } from 'react';
import {Add,  Edit} from '@mui/icons-material';
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SwipeableDrawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import jwtAuthAxios from 'app/services/auth/jwtAuth';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';
import { useFormik } from 'formik';
import * as yup from 'yup';
import TableSkeleton from 'app/shared/widgets/TableSkeleton';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';

const validationSchema = yup.object({
  companyName: yup.string().required('Company Name is required'),
  companyAddress: yup.string().required('Company Address is required'),
  companyCity: yup.string().required('Company City is required'),
  companyCountry: yup.string().required('Company Country is required'),
  companyPAN: yup.string().required('Company PAN is required'),
  companyIsActive: yup.boolean().required('Company IsActive is required'),
});

const Company = () => {
  const [companiesData, setCompaniesData] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [isedit, setIsedit] = useState(false);
  const [loading, setLoading] = useState(false);
  const {rights} = useJumboAuth()
  const swal = useSwalWrapper();

  const formik = useFormik({
    initialValues: {
      companyName: '',
      companyAddress: '',
      companyCity: '',
      companyCountry: '',
      companyPAN: '',
      companyIsActive: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // console.log(values);
      setLoading(true);
      const endpoint = isedit
        ? `company/companies/${values.companyCode}`
        : 'company/addcompany';
      try {
        const response = await jwtAuthAxios.post(endpoint, values);
        // console.log(response.data);
        if (response.data.status === 'success') {
          fetchCompaniesData();
          setDrawer(false);
          swal.fire({
            icon: 'success',
            text: response.data.message,
          });
        } else {
          swal.fire({
            icon: 'error',
            text: response.data.message || 'Something Went Wrong',
          });
        }
      } catch (error) {
        console.error(error);
        swal.fire({
          icon: 'error',
          text: 'Something Went Wrong',
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchCompaniesData = async () => {
    try {
      const response = await jwtAuthAxios.get('company/allcompanies');
      setCompaniesData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCompaniesData();
  }, []);

  useEffect(() => {
    if (drawer === false) {
      formik.resetForm();
      setIsedit(false);
    }
  }, [drawer]);

  const companyEditHandler = (company) => {
    // console.log(company);
    formik.setValues({
      companyName: company?.FL_COMPANY_NAME || '',
      companyAddress: company?.FL_COMPANY_ADDR || '',
      companyCity: company?.FL_COMPANY_CITY || '',
      companyCountry: company?.FL_COMPANY_COUNTRY || '',
      companyPAN: company?.FL_PAN_NO || '',
      companyIsActive: Boolean(company?.FL_IS_ACTIVE),
      companyCode:company?.FL_COMPANY_CODE
    });
    setIsedit(true);
    setDrawer(true);
  };

  return (
    <div>
     {rights.insert==1&& <LoadingButton
        variant="contained"
        onClick={() => setDrawer(true)}
        sx={{ display: 'flex', margin: '0 0px 22px auto' }}
        startIcon={<Add />}
        color="primary"
      >
        Add Company
      </LoadingButton>}
      {companiesData.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Company Name</TableCell>
                <TableCell>Company Address</TableCell>
                <TableCell>Company City</TableCell>
                <TableCell>Company Country</TableCell>
                <TableCell>PAN Number</TableCell>
                <TableCell>Is Active</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companiesData.map((company, index) => (
                <TableRow key={index}>
                  <TableCell>{company.FL_COMPANY_NAME}</TableCell>
                  <TableCell>{company.FL_COMPANY_ADDR}</TableCell>
                  <TableCell>{company.FL_COMPANY_CITY}</TableCell>
                  <TableCell>{company.FL_COMPANY_COUNTRY}</TableCell>
                  <TableCell>{company.FL_PAN_NO}</TableCell>
                  <TableCell>{company.FL_IS_ACTIVE ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell align="right">
                   {rights.edit ==1&&  <Chip
                      color={'error'}
                      onClick={() => companyEditHandler(company)}
                      icon={<Edit fontSize="18px" />}
                      label="edit"
                    />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ):<TableSkeleton cols={7} />}

      <SwipeableDrawer
        anchor="right"
        open={drawer}
        onClose={() => setDrawer(false)}
        onOpen={() => setDrawer(true)}
      >
        <Box
          component="form"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: '400px',
            margin: '0 auto',
            p: 3,
          }}
          onSubmit={formik.handleSubmit}
        >
          <h3 style={{ padding: '5px', marginTop: '0' }}>Company</h3>
          <TextField
            label="Company Name"
            variant="outlined"
            margin="normal"
            fullWidth
            {...formik.getFieldProps('companyName')}
            error={formik.touched.companyName && Boolean(formik.errors.companyName)}
            helperText={formik.touched.companyName && formik.errors.companyName}
          />
          <TextField
            label="Company Address"
            variant="outlined"
            margin="normal"
            fullWidth
            {...formik.getFieldProps('companyAddress')}
            error={formik.touched.companyAddress && Boolean(formik.errors.companyAddress)}
            helperText={formik.touched.companyAddress && formik.errors.companyAddress}
          />
          <TextField
            label="Company City"
            variant="outlined"
            margin="normal"
            fullWidth
            {...formik.getFieldProps('companyCity')}
            error={formik.touched.companyCity && Boolean(formik.errors.companyCity)}
            helperText={formik.touched.companyCity && formik.errors.companyCity}
          />
          <TextField
            label="Company Country"
            variant="outlined"
            margin="normal"
            fullWidth
            {...formik.getFieldProps('companyCountry')}
            error={formik.touched.companyCountry && Boolean(formik.errors.companyCountry)}
            helperText={formik.touched.companyCountry && formik.errors.companyCountry}
          />
          <TextField
            label="PAN Number"
            variant="outlined"
            margin="normal"
            fullWidth
            {...formik.getFieldProps('companyPAN')}
            error={formik.touched.companyPAN && Boolean(formik.errors.companyPAN)}
            helperText={formik.touched.companyPAN && formik.errors.companyPAN}
          />
          <FormControl variant="outlined" fullWidth margin="normal">
            <InputLabel id="isActive-label">Is Active</InputLabel>
            <Select
              labelId="isActive-label"
              label="Is Active"
              {...formik.getFieldProps('companyIsActive')}
              error={formik.touched.companyIsActive && Boolean(formik.errors.companyIsActive)}
              helperText={formik.touched.companyIsActive && formik.errors.companyIsActive}
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </Select>
            {formik.touched.companyIsActive && formik.errors.companyIsActive && (
              <p className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained css-1qhtpql-MuiFormHelperText-root">
                {formik.touched.companyIsActive && formik.errors.companyIsActive}
              </p>
            )}
          </FormControl>
          <LoadingButton
            loading={loading}
            type="submit"
            variant="contained"
            color="primary"
            sx={{ marginTop: '16px' }}
          >
            Submit
          </LoadingButton>
        </Box>
      </SwipeableDrawer>
    </div>
  );
};
Company.Modulename = 'Company'
export default Company;
